import { ChildModel, ModelBase } from 'store/model-base';
import { LineDevicePaymentPlan } from './dpp';
import { DeviceStatus, DeviceType, SimType, SupportDeviceSectionId } from 'my-account/constants';
import { OrderDetail, OrderItem, OrderShipment } from 'store/order/models';
import { DeviceBrand, EligibilityStatus, PlanEightXMCPlanCodes, PlanEightXMCPlanIds, PlanEightXMCPlanNames } from 'core/constants';

export class LineDevice extends ModelBase {
    public static storeName: string = 'lineDevice';
    public static idAttribute: string = 'id.key';

    public id: Id;
    public imei: string;
    public iccId: string;
    public deviceType: string;
    public status: DeviceStatus;
    public byod: boolean;
    public simType: string;
    public orderId: Id;
    public productDetails: LineDeviceDetail;
    public upgradeEligible: boolean;
    public upgradeEligibilityStatus: string;
    public devicePaymentPlan: LineDevicePaymentPlan;
    public insurancePlan: InsurancePlan;
    public trackingId: string; // not coming back from api yet
    public orderDetail: OrderDetail;
    public shipment: OrderShipment;
    public subsidy: DeviceSubsidy;

    public isPhone: boolean;
    public isTablet: boolean;
    public isWatch: boolean;
    public isActive: boolean;
    public isPending: boolean;
    public isNDEL: boolean;
    public isUpgrade: boolean;
    public isExchange: boolean;
    public isWarranty: boolean;
    public isDppOutstanding: boolean;
    public isApple: boolean;
    public isGalaxy: boolean;
    public isPixel: boolean;
    public isMotorola: boolean;
    public isByodExchange: boolean;
    public isReplacementDevice: boolean;
    public name: string;
    public description: string;
    public notBeenActivated: boolean;
    public balancePaid: boolean;
    public hasProtectionPlan: boolean;
    public lineDisplayName: string;
    public isValid: boolean;
    public isEsim: boolean;
    public isPsim: boolean;
    public hasEsim: boolean;
    public lineId: Id;
    public hasSubsidy: boolean;
    public hasTradeInPromo: boolean;
    public is5GCapable: boolean;
    public isNewSim: boolean;
    public isDeviceUpgradeEligible: boolean;
    public isUpgradeEligibleStatus: boolean;
    public isWatchesOrTablets: boolean;
    public hasXMC1: boolean;
    public hasXMC2: boolean;
    public hasXMC_NY: boolean;
    public xmcPlanName: string;

    // eslint-disable-next-line complexity
    public static create<T extends ModelBase>(data: ApiResponse): T {
        data.shipment = data.orderDetail && data.orderDetail.shipments && data.orderDetail.shipments[0];
        data.isPhone = data.deviceType === DeviceType.PHONE;
        data.isTablet = data.deviceType === DeviceType.TABLET;
        data.isWatch = data.deviceType === DeviceType.SMART_WATCH;
        data.isActive = data.status === DeviceStatus.ACTIVE;
        data.isPending = data.status === DeviceStatus.PENDING;
        data.isNDEL = data.status === DeviceStatus.NDEL;
        data.isByodExchange = data.status === DeviceStatus.BYOD_EXCHANGE;
        data.isUpgrade = data.status === DeviceStatus.UPGRADE;
        data.isExchange = data.status === DeviceStatus.EXCHANGE;
        data.isWarranty = data.status === DeviceStatus.WARRANTY;
        data.isDppOutstanding = data.status === DeviceStatus.DPP_OUTSTANDING;
        data.isNewSim = data.status === DeviceStatus.NEW_SIM;
        data.isReplacementDevice = data.isNDEL || data.isExchange || data.isWarranty || data.isUpgrade || data.isByodExchange;
        data.isValid = data.id && data.lineId;
        data.hasSubsidy = Boolean(data.subsidy);
        data.isEsim = data.simType === SimType.ESIM;
        data.isPsim = data.simType === SimType.PSIM;
        data.hasEsim = data.simType === SimType.ESIM || data.simType === SimType.PSIM_ESIM;
        data.hasTradeInPromo = data.hasSubsidy && data.subsidy.tradeinOfferId;

        if (data.productDetails) {
            data.name = data.productDetails.brand && data.productDetails.model ? `${data.productDetails.brand} ${data.productDetails.model}` : '';
            data.description = `${data.productDetails.model}, ${data.productDetails.colorName}, ${data.productDetails.capacity}`;
            data.isApple = data.productDetails.brand && data.productDetails.brand.toLowerCase() === DeviceBrand.APPLE;
            data.isGalaxy = data.productDetails.brand && data.productDetails.brand.toLowerCase() === DeviceBrand.SAMSUNG;
            data.isPixel = data.productDetails.brand && data.productDetails.brand.toLowerCase() === DeviceBrand.GOOGLE;
            data.isMotorola = data.productDetails.brand && data.productDetails.brand.toLowerCase() === DeviceBrand.MOTOROLA;
            data.is5GCapable = data.productDetails.is5GCapable;

            if (data.productDetails.capacity) {
                data.productDetails.capacity = data.productDetails.capacity.toString().toUpperCase();

                if (!data.productDetails.capacity.endsWith('GB')) {
                    data.productDetails.capacity = `${data.productDetails.capacity}GB`;
                }
            }
        }

        data.balancePaid = !data.devicePaymentPlan || !data.devicePaymentPlan.balance;
        data.hasProtectionPlan = Boolean(data.insurancePlan);
        data.notBeenActivated = data.isPending || data.isNDEL || data.isUpgrade || data.isExchange;
        data.isDeviceUpgradeEligible = data.upgradeEligibilityStatus !== EligibilityStatus.UPGRADE_INELIGIBLE;
        data.isUpgradeEligibleStatus = data.isPhone && (data.balancePaid || data.byod) && data.upgradeEligibilityStatus === EligibilityStatus.UPGRADE_ELIGIBLE;
        data.isWatchesOrTablets = (data.isTablet || data.isWatch);
        data.hasXMC1 = data.hasProtectionPlan && PlanEightXMCPlanIds.XMC1.includes(data.insurancePlan.id.value);
        data.hasXMC2 = data.hasProtectionPlan && data.insurancePlan.id.value === PlanEightXMCPlanCodes.XMC2;
        data.hasXMC_NY = data.hasProtectionPlan && data.insurancePlan.id.value && PlanEightXMCPlanIds.XMC_NY.includes(data.insurancePlan.id.value);
        data.xmcPlanName = data.hasXMC1 ? PlanEightXMCPlanNames.XMC1 : (data.hasXMC2 || data.hasXMC_NY) ? PlanEightXMCPlanNames.XMC2 : '';

        return super.create<T>(data);
    }

    protected static get hasOne(): ChildModel[] {
        return [{
            attrName: 'devicePaymentPlan',
            model: LineDevicePaymentPlan
        }, {
            attrName: 'orderDetail',
            model: OrderDetail
        }, {
            attrName: 'shipment',
            model: OrderShipment
        }];
    }

    public get isShipped(): boolean {
        return this.shipment.isShipped;
    }

    public get isDelivered(): boolean {
        return this.shipment.isDelivered || this.orderDetail.isRetailScan || this.orderDetail.isSimLedByod || (this.byod && this.isNDEL) || (this.byod && this.isEsim);
    }

    public get supportDeviceSectionId(): string {
        switch (this.productDetails.brand.toLowerCase()) {
            case DeviceBrand.APPLE:
                return SupportDeviceSectionId.APPLE;
            case DeviceBrand.LG:
                return SupportDeviceSectionId.LG;
            case DeviceBrand.SAMSUNG:
                return SupportDeviceSectionId.SAMSUNG;
            case DeviceBrand.GOOGLE:
                return SupportDeviceSectionId.GOOGLE;
            case DeviceBrand.OTHER:
                return SupportDeviceSectionId.OTHER;
        }
    }

    public getByodCarrier(): string {
        if (!this.orderDetail) {
            return '';
        }

        const orderItem: OrderItem = this.orderDetail.orderItemsByByodDevice.find((item: OrderItem) => item.deviceIccid === this.iccId);

        return orderItem && orderItem.byodCarrier;
    }
}
